import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AuthLayout, { GoogleButton, Divider, Field, SubmitBtn } from "./AuthLayout";

// Where a signed-out user was headed before ProtectedRoute bounced them here
// (e.g. an email "continue" link to /onboarding?job=<id>) — resume there
// after login instead of always dropping them on the dashboard.
function fromPath(location) {
  const from = location.state?.from;
  if (!from?.pathname) return null;
  return from.pathname + (from.search || "");
}

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || String(e)).join(" ");
  return String(detail);
}

export default function EmailLogin() {
  const nav = useNavigate();
  const location = useLocation();
  const { refreshAuth } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const handle = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.post("/auth/login", { email: form.email, password: form.password });
      // Refresh AuthContext so ProtectedRoute sees the new user immediately.
      await refreshAuth();
      nav(fromPath(location) || "/app/dashboard", { replace: true });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        // needs verification — carry the resume target through so VerifyOtp
        // can send them back to it once verified, instead of always the dashboard.
        nav(`/verify-otp?email=${encodeURIComponent(form.email.toLowerCase().trim())}`, {
          state: { from: location.state?.from },
        });
        return;
      }
      setErr(formatError(e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome, founder."
      subtitle="Sign in with email or continue with Google."
      footer={
        <>
          New here?{" "}
          <Link to="/signup" className="font-semibold text-lm-primary hover:text-lm-primaryHover underline underline-offset-4">
            Create an account
          </Link>
        </>
      }
    >
      <GoogleButton />
      <Divider />
      <form onSubmit={handle} data-testid="login-form">
        <Field label="Email" id="login-email" name="email" type="email" required autoComplete="email"
          placeholder="you@company.com"
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="login-email" />
        <Field label="Password" id="login-password" name="password" type="password" required autoComplete="current-password"
          placeholder="Your password"
          hint={<Link to="/forgot-password" className="text-lm-primary hover:text-lm-primaryHover underline underline-offset-4">Forgot?</Link>}
          value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
          data-testid="login-password" />
        {err && <div className="mb-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{err}</div>}
        <SubmitBtn loading={busy} data-testid="login-submit">Sign in</SubmitBtn>
      </form>
    </AuthLayout>
  );
}
